import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { cn } from "@/lib/utils";
import Index from "./pages/Index";
import Feed from "./pages/Feed";
import Playlists from "./pages/Playlists";
import Descubrimientos from "./pages/Descubrimientos";
import Contacto from "./pages/Contacto";
import EnVivo from "./pages/EnVivo";
import PostDetail from "./pages/PostDetail";
import NotFound from "./pages/NotFound";

/**
 * Rutas que no entran en la navegacion normal del visitante. Admin.tsx son
 * 127 KB de los 266 que ocupa src/pages en el bundle, y hasta ahora se lo
 * descargaba todo el mundo para ver la portada.
 *
 * Las paginas de navegacion (portada, feed, playlists, articulos, blogs,
 * contacto y el detalle) siguen siendo estaticas a proposito: la
 * restauracion de scroll de mas abajo da por hecho que la ruta pinta en el
 * primer render, y partirlas meteria un hueco de Suspense justo ahi.
 */
const Login = lazy(() => import("./pages/Login"));
const Admin = lazy(() => import("./pages/Admin"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AvisoLegal = lazy(() => import("./pages/AvisoLegal"));
const Privacidad = lazy(() => import("./pages/Privacidad"));
const Cookies = lazy(() => import("./pages/Cookies"));
import { readSavedScrollY, restoreWindowScrollStable, scrollRestoreStorageKey, shouldPersistScrollRestore } from "@/lib/scrollRestore";
import { resolveRouteSeo, setDocumentSeo } from "@/lib/seoDocument";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const AppShell = () => {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const prevPathnameRef = useRef(pathname);
  const restoreCleanupRef = useRef<null | (() => void)>(null);
  const isHome = pathname === "/";
  const instantScrollTo = (top: number) => {
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo(0, top);
    requestAnimationFrame(() => {
      root.style.scrollBehavior = previousBehavior;
    });
  };

  useEffect(() => {
    if (typeof window === "undefined" || !("scrollRestoration" in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    setDocumentSeo(resolveRouteSeo(pathname, search));
  }, [pathname, search]);

  useLayoutEffect(() => {
    // Si hay una restauración "en curso" de la navegación anterior, cancelarla.
    restoreCleanupRef.current?.();
    restoreCleanupRef.current = null;

    const prevPath = prevPathnameRef.current;
    if (prevPath !== pathname && shouldPersistScrollRestore(prevPath)) {
      try {
        sessionStorage.setItem(scrollRestoreStorageKey(prevPath), String(Math.round(window.scrollY)));
      } catch {
        /* ignore */
      }
    }

    const listRestore = shouldPersistScrollRestore(pathname);
    const key = scrollRestoreStorageKey(pathname);
    const pathnameChanged = prevPath !== pathname;

    if (navigationType === "PUSH") {
      // PUSH también ocurre cuando cambiamos search params en la misma página.
      // Sólo tratamos como “navegación real” (y por tanto scroll arriba / reset) si cambia el pathname.
      if (pathnameChanged && listRestore) {
        try {
          sessionStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      }
      if (pathnameChanged) instantScrollTo(0);
      prevPathnameRef.current = pathname;
      return;
    }

    if (navigationType === "REPLACE") {
      // REPLACE se usa en la app para sync de filtros/querystring:
      // si no cambia el pathname, NO forzamos scroll a 0.
      if (pathnameChanged) instantScrollTo(0);
      prevPathnameRef.current = pathname;
      return;
    }

    if (navigationType === "POP" && listRestore) {
      // /articulos usa restauración por ancla dentro de la propia página (más exacta con infinite/lazy),
      // así que evitamos una restauración global que podría pelearse y dejarte “un poco arriba”.
      if (pathname === "/articulos") {
        try {
          if (sessionStorage.getItem("articulos-scroll-anchor:post")) {
            prevPathnameRef.current = pathname;
            return;
          }
        } catch {
          /* ignore */
        }
      }
      const y = readSavedScrollY(pathname);
      if (y != null) {
        restoreCleanupRef.current = restoreWindowScrollStable(y);
        prevPathnameRef.current = pathname;
        return;
      }
    }

    if (pathnameChanged) instantScrollTo(0);
    prevPathnameRef.current = pathname;
  }, [pathname, search, navigationType]);

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden">
      <Navbar />
      <main className={cn("flex-1", !isHome && "pt-14 md:pt-16")}>
        {/* El hueco conserva alto para que no salte el layout al resolver. */}
        <Suspense fallback={<div className="min-h-[60vh]" aria-hidden />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/playlist" element={<Navigate to="/playlists" replace />} />
          <Route path="/playlists" element={<Playlists />} />
          <Route path="/articulos" element={<Descubrimientos />} />
          <Route path="/blogs" element={<EnVivo />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/post/:kind/:id" element={<PostDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

/**
 * Sin TooltipProvider: la web no pinta ni un tooltip. Los unicos que los
 * usaban eran ui/sidebar.tsx y ui/chart.tsx, boilerplate de shadcn que no
 * importa nadie. El proveedor arrastraba @radix-ui/react-tooltip y
 * @floating-ui (core y dom): 86 KB sin minificar en el chunk inicial para
 * una funcion que no existe.
 *
 * Si algun dia se añade un <Tooltip>, hay que volver a envolver aqui: Radix
 * lo exige y avisa por consola si falta.
 */
const App = () => (
  <QueryClientProvider client={queryClient}>
    <Sonner />
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
